import { AlertTriangle } from 'lucide-react'
import type { ReactElement } from 'react'

/**
 * What auto-order actually does, said plainly before an admin turns it on.
 *
 * PROD-365 (Wesley, Ops): admins could not tell whether auto-order places
 * real orders or just stages a cart, nor whether one estimate covered every
 * mealtime. Both answers were only discoverable by reading the runner:
 *
 *   - A fill job stages the cart up to 7 days ahead, and a submit job turns
 *     it into a REAL, CHARGED order as soon as the campus's ordering
 *     deadline passes. There is no opt-out — `autoSubmit` is always on.
 *   - One auto-order covers exactly one mealtime, each with its own
 *     quantity, so Breakfast and Lunch differ by having two auto-orders.
 *
 * Deliberately blunt about the charge. This runs unattended against a
 * school's money, and the previous copy ("so cafeteria days run
 * themselves") let an admin assume a human step existed that does not.
 */
export function AutoOrderExplainer({ compact = false }: { compact?: boolean }): ReactElement {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        Auto-orders place real orders
      </p>
      <ul className="mt-1.5 space-y-1">
        <li>
          Your cart is filled up to a week ahead, then <span className="text-foreground">submitted automatically</span>{' '}
          when your ordering deadline passes — and your account is charged. This is not a draft.
        </li>
        <li>
          The window between the cart being filled and your deadline is your time to change or cancel it.{' '}
          <span className="text-foreground">If you do nothing, the order is placed.</span>
        </li>
        {compact ? null : (
          <>
            <li>
              Each auto-order covers <span className="text-foreground">one mealtime</span>. Add a second for Breakfast
              alongside Lunch — each carries its own quantity.
            </li>
            <li>
              You set how many <span className="text-foreground">meals</span> to order. Every component ships at your
              campus&rsquo;s configured percentage for that mealtime.
            </li>
            <li>
              If an order can&rsquo;t be placed, it shows up as a task in Ordo and retries for two weeks —{' '}
              <span className="text-foreground">no email is sent</span>.
            </li>
          </>
        )}
      </ul>
    </div>
  )
}
